import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Car, Calendar, Gauge, Shield, MapPin, Eye, ExternalLink, User, Mail, Phone, Loader2, Images, ChevronLeft, ChevronRight } from 'lucide-react';
import { Lightbox } from '@/components/Lightbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicListingPage() {
  const { listingId } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/marketplace/public/listings/${listingId}`);
        if (!res.ok) throw new Error();
        setListing(await res.json());
      } catch { setError(true); }
      finally { setLoading(false); }
    })();
  }, [listingId]);

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#c41e1e]" />
    </div>
  );

  if (error || !listing) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-800">Inserat nicht gefunden</h1>
        <p className="text-gray-500 mt-2">Dieses Inserat ist nicht mehr verfügbar.</p>
      </div>
    </div>
  );

  const imgs = listing.images || [];
  const isSell = listing.type === 'sell';
  const isSold = listing.status === 'sold';
  const contact = listing.contact_info;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#c41e1e] text-white py-3 px-4 text-center text-sm font-medium tracking-wider uppercase">
        S.H.R.T. – Swiss Historic Racing Team · Marktplatz
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Images */}
        {imgs.length > 0 && (
          <div className="mb-6">
            <div className="relative aspect-[16/9] sm:aspect-[4/3] overflow-hidden bg-gray-100 cursor-pointer" onClick={() => setLightboxIdx(currentImage)}>
              <img src={imgs[currentImage]?.url} alt={listing.title} className="w-full h-full object-contain bg-black" />
              {isSold && <span className="absolute top-3 left-3 bg-green-600 text-white text-xs font-bold uppercase px-2 py-1">Verkauft</span>}
              {!isSold && <span className={`absolute top-3 left-3 text-xs font-bold uppercase px-2 py-1 ${isSell ? 'bg-[#c41e1e] text-white' : 'bg-blue-500 text-white'}`}>{isSell ? 'Verkauf' : 'Suche'}</span>}
              {imgs.length > 1 && (
                <>
                  <button onClick={e => { e.stopPropagation(); setCurrentImage(i => i > 0 ? i - 1 : imgs.length - 1); }} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white flex items-center justify-center"><ChevronLeft size={18} /></button>
                  <button onClick={e => { e.stopPropagation(); setCurrentImage(i => i < imgs.length - 1 ? i + 1 : 0); }} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white flex items-center justify-center"><ChevronRight size={18} /></button>
                  <span className="absolute bottom-2 right-2 text-xs text-white bg-black/60 px-2 py-1 flex items-center gap-1"><Images size={12} /> {currentImage + 1}/{imgs.length}</span>
                </>
              )}
            </div>
            {imgs.length > 1 && (
              <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1">
                {imgs.map((img, i) => (
                  <button key={i} onClick={() => setCurrentImage(i)} className={`flex-shrink-0 w-14 h-14 overflow-hidden border-2 ${i === currentImage ? 'border-[#c41e1e]' : 'border-gray-200 opacity-60 hover:opacity-100'}`}>
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Title + Details */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Oswald, sans-serif' }}>{listing.title}</h1>
          <div className="mt-3 space-y-1.5">
            {listing.brand && <div className="flex items-center gap-2 text-sm text-gray-700"><Car size={14} className="text-gray-400" />{listing.brand} {listing.model}</div>}
            {isSell && listing.year && <div className="flex items-center gap-2 text-sm text-gray-700"><Calendar size={14} className="text-gray-400" />Baujahr {listing.year}</div>}
            {listing.mileage && <div className="flex items-center gap-2 text-sm text-gray-700"><Gauge size={14} className="text-gray-400" />{listing.mileage}</div>}
            {listing.condition && <div className="flex items-center gap-2 text-sm text-gray-700"><Shield size={14} className="text-gray-400" />{listing.condition}</div>}
            {listing.location && <div className="flex items-center gap-2 text-sm text-gray-700"><MapPin size={14} className="text-gray-400" />{listing.location}</div>}
            {listing.viewing_possible && <div className="flex items-center gap-2 text-sm text-green-700"><Eye size={14} className="text-green-500" />Besichtigung möglich</div>}
            {listing.external_link && (
              <a href={listing.external_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#c41e1e] hover:underline" data-testid="external-link">
                <ExternalLink size={14} />Externes Inserat ansehen
              </a>
            )}
          </div>
        </div>

        {/* Price */}
        <div className={`p-4 text-center mb-6 ${isSold ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <span className={`text-2xl font-bold ${isSold ? 'text-green-700 line-through' : 'text-[#c41e1e]'}`}>
            {isSell ? (listing.price_on_request || listing.price == null ? 'Preis auf Anfrage' : `CHF ${listing.price.toLocaleString('de-CH')}`) : (listing.budget_max ? `Budget: CHF ${listing.budget_max.toLocaleString('de-CH')}` : 'Budget offen')}
          </span>
          {isSold && <p className="text-green-700 text-sm font-semibold mt-1">Verkauft</p>}
        </div>

        {/* Description */}
        {listing.description && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>BESCHREIBUNG</h2>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{listing.description}</p>
          </div>
        )}

        {/* Contact info */}
        {contact && (
          <div className="bg-gray-50 border border-gray-200 p-5 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>KONTAKT</h2>
            <div className="space-y-2">
              {contact.name && <div className="flex items-center gap-2 text-sm"><User size={14} className="text-gray-400" />{contact.name}</div>}
              {contact.email && <div className="flex items-center gap-2 text-sm"><Mail size={14} className="text-gray-400" /><a href={`mailto:${contact.email}`} className="text-[#c41e1e] hover:underline">{contact.email}</a></div>}
              {contact.phone && <div className="flex items-center gap-2 text-sm"><Phone size={14} className="text-gray-400" /><a href={`tel:${contact.phone}`} className="text-[#c41e1e] hover:underline">{contact.phone}</a></div>}
            </div>
          </div>
        )}

        {/* CTA */}
        {!isSold && (
          <div className="text-center py-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-3">Interesse? Kontaktiere den Verkäufer oder werde S.H.R.T. Mitglied.</p>
            <a href="https://member.shrt.ch/mitglied-werden" className="inline-block bg-[#c41e1e] text-white px-6 py-2 text-sm font-semibold uppercase tracking-wider hover:bg-[#a51919] transition-colors">Mitglied werden</a>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 py-6 border-t border-gray-100">
          <p>S.H.R.T. – Swiss Historic Racing Team</p>
          <p className="mt-1">
            <a href="https://member.shrt.ch/datenschutz" className="hover:text-gray-600">Datenschutz</a> · <a href="https://member.shrt.ch/agb" className="hover:text-gray-600">AGB</a> · <a href="https://member.shrt.ch/impressum" className="hover:text-gray-600">Impressum</a>
          </p>
        </div>
      </div>

      {lightboxIdx >= 0 && (
        <Lightbox images={imgs} startIndex={lightboxIdx} onClose={() => setLightboxIdx(-1)} />
      )}
    </div>
  );
}
